import reading from './raamEnclosuresReadings.json';
import type { BuildContext, Obstacle } from '../types';
import type { PolygonWithHoles, Vec2 } from '../../geo/types';
import { bufferPolyline, fromRing, inflate, union } from '../../geo/clip';
import { pointInPolygon, segmentIntersection } from '../../geo/polygon';
import { createGroundElevation } from '../../site/groundElevation';
import { sampleSwatch } from '../../data/palette';
import { layoutToWorld } from '../../core/coords';
import { edgeIdFor, SURFACE } from '../mesh/attributes';
import type { Frame, VertexStyle } from '../mesh/meshBuilder';
import { beamSolid, boxSolid, planPoint } from './solids';

export interface GardenFenceRun {
  readonly id: string;
  readonly kind: 'boundary' | 'division';
  readonly a: Vec2;
  readonly b: Vec2;
}

export interface GardenEntrance {
  readonly garden: string;
  readonly at: Vec2;
  readonly inward: Vec2;
  readonly tangent: Vec2;
}

const mid = (a: Vec2, b: Vec2): Vec2 => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
const along = (a: Vec2, b: Vec2, t: number): Vec2 => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
];

/** Clip the fence centreline, so its beams and collision footprint use the same open intervals. */
function clearRuns(run: GardenFenceRun, blockers: readonly PolygonWithHoles[]): GardenFenceRun[] {
  const breaks = [0, 1];
  for (const p of blockers) {
    for (const ring of [p.outer, ...p.holes]) {
      ring.forEach((a, i) => {
        const hit = segmentIntersection(run, { a, b: ring[(i + 1) % ring.length]! });
        if (hit) breaks.push(hit.tS);
      });
    }
  }
  const stations = [...new Set(breaks)].sort((a, b) => a - b),
    length = Math.hypot(run.b[0] - run.a[0], run.b[1] - run.a[1]);
  return stations.slice(1).flatMap((to, i) => {
    const from = stations[i]!;
    if (
      (to - from) * length < 0.2 ||
      blockers.some((p) => pointInPolygon(along(run.a, run.b, (from + to) / 2), p))
    )
      return [];
    return [
      { ...run, id: `${run.id}/${i}`, a: along(run.a, run.b, from), b: along(run.a, run.b, to) },
    ];
  });
}

/** Fitted enclosure edges; their 1538 dimensions and four canal entrances remain estimated. */
export function planRaamEnclosures(ctx: Pick<BuildContext, 'layout' | 'lots'>) {
  const gardens = reading.gardens.map((r) => ctx.layout.gardens.find((g) => g.id === r.id));
  if (gardens.some((g) => !g)) return { runs: [], entrances: [] };
  const polygons: PolygonWithHoles[] = gardens.map((g) => fromRing(g!.polygon)),
    divisions: GardenFenceRun[] = [],
    entrances: GardenEntrance[] = [];
  reading.gardens.forEach((r, i) => {
    const ring = gardens[i]!.polygon,
      a = ring[r.front[0]!]!,
      b = ring[r.front[1]!]!,
      length = Math.hypot(b[0] - a[0], b[1] - a[1]),
      tangent: Vec2 = [(b[0] - a[0]) / length, (b[1] - a[1]) / length];
    entrances.push({ garden: r.id, at: mid(a, b), tangent, inward: [-tangent[1], tangent[0]] });
    if (!r.division || !r.nextDivision) return;
    const next = gardens[i + 1]!.polygon,
      near = ring[r.division[0]!]!,
      far = ring[r.division[1]!]!,
      nextNear = next[r.nextDivision[0]!]!,
      nextFar = next[r.nextDivision[1]!]!;
    polygons.push(fromRing([near, far, nextFar, nextNear]));
    divisions.push({
      id: `raam-division-${i + 1}`,
      kind: 'division',
      a: mid(near, nextNear),
      b: mid(far, nextFar),
    });
  });
  const compound = inflate(union(polygons), -reading.inset, 'miter'),
    boundaries = compound.flatMap((p, j) =>
      p.outer.map((a, i): GardenFenceRun => ({
        id: `raam-boundary-${j}-${i}`,
        kind: 'boundary',
        a,
        b: p.outer[(i + 1) % p.outer.length]!,
      }))
    ),
    houses = [...ctx.lots.houses, ...ctx.lots.backhouses].filter((l) =>
      l.reconstruction?.id.startsWith('raam-')
    ),
    openings = entrances.flatMap((e) =>
      bufferPolyline(
        [
          along(e.at, [e.at[0] + e.inward[0], e.at[1] + e.inward[1]], -1),
          along(e.at, [e.at[0] + e.inward[0], e.at[1] + e.inward[1]], 1),
        ],
        reading.openingWidth / 2,
        'butt'
      )
    ),
    blockers = [
      ...inflate(
        houses.map((l) => fromRing(l.corners)),
        reading.houseClearance,
        'miter'
      ),
      ...openings,
    ];
  return { runs: [...boundaries, ...divisions].flatMap((r) => clearRuns(r, blockers)), entrances };
}

export function raamEnclosureObstacles(runs: readonly GardenFenceRun[]): Obstacle[] {
  return runs.flatMap((r) =>
    bufferPolyline(
      [r.a, r.b],
      Math.max(reading.postHalf * Math.SQRT2, 0.05 + reading.railHalf),
      'round'
    ).map((p): Obstacle => ({
      id: r.id,
      kind: 'structure',
      polygon: p.outer,
    }))
  );
}

export function buildRaamEnclosures(ctx: BuildContext) {
  const plan = planRaamEnclosures(ctx),
    elevation = createGroundElevation(ctx.layout);
  let triangles = 0;
  plan.runs.forEach((r, i) => {
    const at = mid(r.a, r.b),
      builder = ctx.chunks.builderAt('fabric', at),
      before = builder.triangleCount,
      length = Math.hypot(r.b[0] - r.a[0], r.b[1] - r.a[1]),
      yaw = Math.atan2(r.b[1] - r.a[1], r.b[0] - r.a[0]),
      style: VertexStyle = {
        color: sampleSwatch(ctx.palette.shipHull, ctx.rng.fork(`raam-timber:${r.id}`)),
        surface: SURFACE.wood,
        edgeId: edgeIdFor(60_000 + i),
        strokeAngle: 0,
        extent: [reading.boardWidth, reading.height],
      },
      frame: Frame = { origin: layoutToWorld(r.a, 0), yaw },
      bays = Math.ceil(length / reading.postSpacing);
    for (let j = 0; j <= bays; j++) {
      const p = along(r.a, r.b, j / bays),
        h = reading.postHalf;
      boxSolid(
        builder,
        style,
        { origin: layoutToWorld(p, elevation(p)), yaw },
        [-h, 0, -h],
        [h, reading.postHeight, h],
        { top: true, bottom: true }
      );
    }
    const boards = Math.max(1, Math.floor((length - 2 * reading.postHalf) / reading.boardSpacing));
    for (let j = 0; j < boards; j++) {
      const p = along(r.a, r.b, (j + 0.5) / boards),
        w = Math.min(reading.boardWidth, (length / boards) * 0.8) / 2,
        t = reading.boardThickness / 2;
      boxSolid(
        builder,
        style,
        { origin: layoutToWorld(p, elevation(p)), yaw },
        [-w, 0, -t],
        [w, reading.height, t],
        { top: true, bottom: true }
      );
    }
    for (const height of reading.railHeights) {
      beamSolid(
        builder,
        style,
        frame,
        planPoint(0, elevation(r.a) + height, 0.05),
        planPoint(length, elevation(r.b) + height, 0.05),
        reading.railHalf
      );
    }
    triangles += builder.triangleCount - before;
  });
  return {
    triangles,
    obstacles: raamEnclosureObstacles(plan.runs),
    runs: plan.runs.length,
    entrances: plan.entrances.length,
  };
}
